import React, { useCallback, useEffect, useState } from 'react';
import { Box, IconButton, Paper, Typography, useMediaQuery, useTheme } from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PageHeader from '../components/layout/PageHeader';
import TopicList from '../components/topics/TopicList';
import TopicDetail from '../components/topics/TopicDetail';
import TopicForm from '../components/topics/TopicForm';
import TaskForm from '../components/tasks/TaskForm';
import ConfirmDialog from '../components/common/ConfirmDialog';
import LoadingSpinner from '../components/common/LoadingSpinner';
import usePageTitle from '../hooks/usePageTitle';
import { Topic, TopicFormData, getTopics, createTopic, updateTopic, deleteTopic } from '../services/topics';
import { Task, TaskFormData, getTasks, createTask, updateTask, deleteTask } from '../services/tasks';
import { Planner, getPlanners } from '../services/planners';
import { Document, getDocuments } from '../services/documents';

const TopicsPage: React.FC = () => {
  usePageTitle('Topics');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [searchParams, setSearchParams] = useSearchParams();

  const [topics, setTopics] = useState<Topic[]>([]);
  const [selectedTopic, setSelectedTopic] = useState<Topic | null>(null);
  const [topicTasks, setTopicTasks] = useState<Task[]>([]);
  const [topicDocs, setTopicDocs] = useState<Document[]>([]);
  const [planners, setPlanners] = useState<Planner[]>([]);
  const [loading, setLoading] = useState(true);

  // Topic form state
  const [topicFormOpen, setTopicFormOpen] = useState(false);
  const [editingTopic, setEditingTopic] = useState<Topic | null>(null);

  // Task form state
  const [taskFormOpen, setTaskFormOpen] = useState(false);
  const [editingTask, setEditingTask] = useState<Task | null>(null);
  const [deleteTaskTarget, setDeleteTaskTarget] = useState<Task | null>(null);

  const [topicCoverPrompt, setTopicCoverPrompt] = useState<{ topic_id: string; topic_name: string } | null>(null);

  const loadTopics = useCallback(async () => {
    try {
      const res = await getTopics();
      setTopics(res.data);
      return res.data;
    } catch (err) {
      console.error(err);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const loadTopicTasks = useCallback(async (topicId: string) => {
    try {
      const res = await getTasks({ topic_id: topicId });
      setTopicTasks(res.data);
      return res.data;
    } catch (err) {
      console.error(err);
      return null;
    }
  }, []);

  useEffect(() => {
    const load = async () => {
      const [freshTopics, pRes] = await Promise.all([loadTopics(), getPlanners()]);
      setPlanners(pRes.data);
      if (!freshTopics || freshTopics.length === 0) return;
      const requestedId = searchParams.get('topic');
      const requested = requestedId ? freshTopics.find((t) => t.id === requestedId) ?? null : null;
      if (requested) {
        setSelectedTopic(requested);
        searchParams.delete('topic');
        setSearchParams(searchParams, { replace: true });
        return;
      }
      if (!selectedTopic && !isMobile) {
        setSelectedTopic(freshTopics[0]);
      }
    };
    load();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadTopics]);

  const loadTopicDocs = useCallback(async (topicId: string) => {
    try {
      const res = await getDocuments();
      setTopicDocs(res.data.filter((d) => d.topic_id === topicId));
    } catch (err) {
      console.error(err);
    }
  }, []);

  useEffect(() => {
    if (selectedTopic) {
      loadTopicTasks(selectedTopic.id);
      loadTopicDocs(selectedTopic.id);
    } else {
      setTopicTasks([]);
      setTopicDocs([]);
    }
  }, [selectedTopic, loadTopicTasks, loadTopicDocs]);

  const handleSelectTopic = (topic: Topic) => {
    setSelectedTopic(topic);
  };

  const handleSaveTopic = async (data: TopicFormData) => {
    try {
      if (editingTopic) {
        const res = await updateTopic(editingTopic.id, data);
        setSelectedTopic(res.data);
      } else {
        const res = await createTopic(data);
        setSelectedTopic(res.data);
      }
      setTopicFormOpen(false);
      setEditingTopic(null);
      loadTopics();
    } catch (err) {
      console.error(err);
    }
  };

  const handleDeleteTopic = async (topic: Topic, cascade: boolean) => {
    try {
      await deleteTopic(topic.id, cascade);
      if (selectedTopic?.id === topic.id) {
        setSelectedTopic(null);
      }
      loadTopics();
    } catch (err) {
      console.error(err);
    }
  };

  const handleDeleteTask = async () => {
    if (!deleteTaskTarget) return;
    try {
      await deleteTask(deleteTaskTarget.id);
      setDeleteTaskTarget(null);
      const freshTopics = await loadTopics();
      if (selectedTopic) {
        loadTopicTasks(selectedTopic.id);
        const updated = freshTopics?.find((t) => t.id === selectedTopic.id);
        if (updated) setSelectedTopic(updated);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleSaveTask = async (data: TaskFormData) => {
    try {
      if (editingTask) {
        const res = await updateTask(editingTask.id, data);
        if (res.data.topic_all_tasks_completed) {
          setTopicCoverPrompt(res.data.topic_all_tasks_completed);
        }
      } else {
        await createTask(data);
      }
      setTaskFormOpen(false);
      setEditingTask(null);
      const freshTopics = await loadTopics();
      if (selectedTopic) {
        loadTopicTasks(selectedTopic.id);
        const updated = freshTopics?.find((t) => t.id === selectedTopic.id);
        if (updated) setSelectedTopic(updated);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleTaskStatusChange = async (task: Task, status: string) => {
    try {
      const res = await updateTask(task.id, { status });
      if (res.data.topic_all_tasks_completed) {
        setTopicCoverPrompt(res.data.topic_all_tasks_completed);
      }
      const freshTopics = await loadTopics();
      if (selectedTopic) {
        loadTopicTasks(selectedTopic.id);
        const updated = freshTopics?.find((t) => t.id === selectedTopic.id);
        if (updated) setSelectedTopic(updated);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleCoverTopic = async () => {
    if (!topicCoverPrompt) return;
    try {
      await updateTopic(topicCoverPrompt.topic_id, { status: 'covered' });
      setTopicCoverPrompt(null);
      const freshTopics = await loadTopics();
      if (selectedTopic && freshTopics) {
        const updated = freshTopics.find((t) => t.id === selectedTopic.id);
        if (updated) setSelectedTopic(updated);
      }
    } catch (err) {
      console.error(err);
    }
  };

  if (loading) return <LoadingSpinner />;

  // Mobile: show detail panel when a topic is selected, list panel otherwise
  const showMobileDetail = isMobile && selectedTopic !== null;
  const showMobileList = isMobile && selectedTopic === null;

  return (
    <>
      <PageHeader title="Topics" actionLabel="New Topic" onAction={() => { setEditingTopic(null); setTopicFormOpen(true); }} />

      {/* Mobile: detail view */}
      {showMobileDetail && (
        <Box sx={{ height: 'calc(100vh - 200px)', display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <IconButton onClick={() => setSelectedTopic(null)} size="small" sx={{ mr: 1 }}>
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="subtitle2" color="text.secondary">Back to Topics</Typography>
          </Box>
          <Paper variant="outlined" sx={{ flexGrow: 1, overflowY: 'auto', borderRadius: 3 }}>
            <TopicDetail
              topic={selectedTopic}
              tasks={topicTasks}
              documents={topicDocs}
              planners={planners}
              onEdit={() => { setEditingTopic(selectedTopic); setTopicFormOpen(true); }}
              onAddTask={() => { setEditingTask(null); setTaskFormOpen(true); }}
              onEditTask={(t) => { setEditingTask(t); setTaskFormOpen(true); }}
              onStatusChange={handleTaskStatusChange}
            />
          </Paper>
        </Box>
      )}

      {/* Mobile: list view */}
      {showMobileList && (
        <Paper
          variant="outlined"
          sx={{ height: 'calc(100vh - 200px)', overflowY: 'auto', borderRadius: 3 }}
        >
          <TopicList
            topics={topics}
            planners={planners}
            selectedTopicId={null}
            onSelect={handleSelectTopic}
          />
        </Paper>
      )}

      {/* Desktop: side-by-side panels (unchanged) */}
      {!isMobile && (
        <Box
          sx={{
            display: 'flex',
            gap: 2,
            height: 'calc(100vh - 200px)',
            flexDirection: 'row',
          }}
        >
          {/* Left panel: topic list */}
          <Paper
            variant="outlined"
            sx={{
              width: 320,
              flexShrink: 0,
              overflowY: 'auto',
              borderRadius: 3,
            }}
          >
            <TopicList
              topics={topics}
              planners={planners}
              selectedTopicId={selectedTopic?.id || null}
              onSelect={handleSelectTopic}
            />
          </Paper>

          {/* Right panel: topic detail */}
          <Paper
            variant="outlined"
            sx={{
              flexGrow: 1,
              overflowY: 'auto',
              borderRadius: 3,
              minHeight: 300,
            }}
          >
            {selectedTopic ? (
              <TopicDetail
                topic={selectedTopic}
                tasks={topicTasks}
                documents={topicDocs}
                planners={planners}
                onEdit={() => { setEditingTopic(selectedTopic); setTopicFormOpen(true); }}
                onAddTask={() => { setEditingTask(null); setTaskFormOpen(true); }}
                onEditTask={(t) => { setEditingTask(t); setTaskFormOpen(true); }}
                onStatusChange={handleTaskStatusChange}
              />
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', p: 3 }}>
                <Typography color="text.secondary">
                  {topics.length > 0 ? 'Select a topic to view details' : 'Create your first topic to get started'}
                </Typography>
              </Box>
            )}
          </Paper>
        </Box>
      )}

      <TopicForm
        open={topicFormOpen}
        topic={editingTopic}
        planners={planners}
        onSave={handleSaveTopic}
        onClose={() => { setTopicFormOpen(false); setEditingTopic(null); }}
        onDelete={handleDeleteTopic}
      />

      <TaskForm
        open={taskFormOpen}
        task={editingTask}
        planners={planners}
        topics={topics}
        defaultTopicId={selectedTopic?.id || null}
        onSave={handleSaveTask}
        onClose={() => { setTaskFormOpen(false); setEditingTask(null); }}
        onDelete={(t) => { setTaskFormOpen(false); setEditingTask(null); setDeleteTaskTarget(t); }}
      />
      <ConfirmDialog
        open={!!deleteTaskTarget}
        title="Delete Task"
        message={`Are you sure you want to delete "${deleteTaskTarget?.title}"?`}
        onConfirm={handleDeleteTask}
        onCancel={() => setDeleteTaskTarget(null)}
      />

      <ConfirmDialog
        open={!!topicCoverPrompt}
        title="All Tasks Completed"
        message={`All tasks under "${topicCoverPrompt?.topic_name}" are completed. Mark this topic as covered?`}
        confirmLabel="Mark Covered"
        confirmColor="success"
        onConfirm={handleCoverTopic}
        onCancel={() => setTopicCoverPrompt(null)}
      />
    </>
  );
};

export default TopicsPage;
